import { useCallback, useRef, useState } from "react"
import {
  addStudentQuestionTag,
  bookmarkStudentQuestion,
  deleteStudentQuestionNote,
  getStudentQuestionDetail,
  removeStudentQuestionTag,
  saveStudentQuestionNote,
  unbookmarkStudentQuestion,
} from "../../../../api/studentQuestions"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import type { DetailTab, StudentQuestionDetail } from "../types"

const ensureStudent = (detail: StudentQuestionDetail) => ({
  is_bookmarked: !!detail.student?.is_bookmarked,
  notes: detail.student?.notes ?? null,
  tags: Array.isArray(detail.student?.tags) ? detail.student.tags : [],
})

const normalizeDetail = (data: StudentQuestionDetail): StudentQuestionDetail => ({
  ...data,
  student: ensureStudent(data),
})

const useQuestionDetail = () => {
  const [detail, setDetail] = useState<StudentQuestionDetail | null>(null)
  const [activeTab, setActiveTab] = useState<DetailTab>("attempts")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [savingNote, setSavingNote] = useState(false)
  const [savingTag, setSavingTag] = useState(false)
  const detailRef = useRef<StudentQuestionDetail | null>(null)
  const requestIdRef = useRef(0)
  detailRef.current = detail

  const open = useCallback(async (questionId: string, tab: DetailTab = "attempts") => {
    const requestId = ++requestIdRef.current
    setLoading(true)
    setError("")
    setActiveTab(tab)
    // Keep previous detail visible only if same question; otherwise clear for loader.
    setDetail((prev) => (prev?.id === questionId ? prev : null))
    try {
      const res = await getStudentQuestionDetail(questionId)
      if (requestId !== requestIdRef.current) return
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Failed to load question")
      }
      setDetail(normalizeDetail(res.data.data))
    } catch (err) {
      if (requestId !== requestIdRef.current) return
      setDetail(null)
      setError(getLearnerApiErrorMessage(err, "Failed to load question"))
    } finally {
      if (requestId === requestIdRef.current) setLoading(false)
    }
  }, [])

  const close = useCallback(() => {
    // Invalidate any in-flight detail fetch so it cannot reopen the drawer.
    requestIdRef.current += 1
    setDetail(null)
    setError("")
    setLoading(false)
    setActiveTab("attempts")
  }, [])

  /** Patch bookmark in open detail without refetch (keeps notes/tags UI intact). */
  const syncBookmark = useCallback((questionId: string, isBookmarked: boolean) => {
    setDetail((prev) => {
      if (!prev || prev.id !== questionId) return prev
      return {
        ...prev,
        student: { ...ensureStudent(prev), is_bookmarked: isBookmarked },
      }
    })
  }, [])

  const toggleBookmark = useCallback(async (): Promise<boolean> => {
    const current = detailRef.current
    if (!current) throw new Error("No question selected")
    const next = !ensureStudent(current).is_bookmarked
    const questionId = current.id

    setDetail((prev) => {
      if (!prev || prev.id !== questionId) return prev
      return {
        ...prev,
        student: { ...ensureStudent(prev), is_bookmarked: next },
      }
    })

    try {
      if (next) await bookmarkStudentQuestion(questionId)
      else await unbookmarkStudentQuestion(questionId)
      return next
    } catch (err) {
      setDetail((prev) => {
        if (!prev || prev.id !== questionId) return prev
        return {
          ...prev,
          student: { ...ensureStudent(prev), is_bookmarked: !next },
        }
      })
      throw err
    }
  }, [])

  const saveNote = useCallback(async (notes: string) => {
    const current = detailRef.current
    if (!current) return
    const questionId = current.id
    setSavingNote(true)
    try {
      const trimmed = notes.trim()
      if (!trimmed) {
        const hadSavedNote = !!current.student?.notes?.trim()
        if (hadSavedNote) {
          await deleteStudentQuestionNote(questionId)
        }
        setDetail((prev) => {
          if (!prev || prev.id !== questionId) return prev
          return {
            ...prev,
            student: { ...ensureStudent(prev), notes: null },
          }
        })
        return
      }
      await saveStudentQuestionNote(questionId, trimmed)
      setDetail((prev) => {
        if (!prev || prev.id !== questionId) return prev
        return {
          ...prev,
          student: { ...ensureStudent(prev), notes: trimmed },
        }
      })
    } finally {
      setSavingNote(false)
    }
  }, [])

  const addTag = useCallback(async (rawTag: string) => {
    const current = detailRef.current
    if (!current) return
    const tag = rawTag.trim().replace(/^#/, "").trim()
    if (!tag) return

    const questionId = current.id
    const previous = ensureStudent(current).tags
    if (previous.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      throw new Error("That tag is already added")
    }
    if (previous.length >= 20) {
      throw new Error("You can add at most 20 tags")
    }

    const optimistic = [...previous, tag]
    setSavingTag(true)
    setDetail((prev) => {
      if (!prev || prev.id !== questionId) return prev
      return {
        ...prev,
        student: { ...ensureStudent(prev), tags: optimistic },
      }
    })

    try {
      const res = await addStudentQuestionTag(questionId, tag)
      const nextTags = res.data?.data?.tags
      setDetail((prev) => {
        if (!prev || prev.id !== questionId) return prev
        return {
          ...prev,
          student: {
            ...ensureStudent(prev),
            tags: Array.isArray(nextTags) ? nextTags : optimistic,
          },
        }
      })
    } catch (err) {
      setDetail((prev) => {
        if (!prev || prev.id !== questionId) return prev
        return {
          ...prev,
          student: { ...ensureStudent(prev), tags: previous },
        }
      })
      throw err
    } finally {
      setSavingTag(false)
    }
  }, [])

  const removeTag = useCallback(async (tag: string) => {
    const current = detailRef.current
    if (!current) return
    const questionId = current.id
    const previous = ensureStudent(current).tags
    const optimistic = previous.filter(
      (t) => t.toLowerCase() !== tag.toLowerCase()
    )

    setSavingTag(true)
    setDetail((prev) => {
      if (!prev || prev.id !== questionId) return prev
      return {
        ...prev,
        student: { ...ensureStudent(prev), tags: optimistic },
      }
    })

    try {
      const res = await removeStudentQuestionTag(questionId, tag)
      const nextTags = res.data?.data?.tags
      setDetail((prev) => {
        if (!prev || prev.id !== questionId) return prev
        return {
          ...prev,
          student: {
            ...ensureStudent(prev),
            tags: Array.isArray(nextTags) ? nextTags : optimistic,
          },
        }
      })
    } catch (err) {
      setDetail((prev) => {
        if (!prev || prev.id !== questionId) return prev
        return {
          ...prev,
          student: { ...ensureStudent(prev), tags: previous },
        }
      })
      throw err
    } finally {
      setSavingTag(false)
    }
  }, [])

  return {
    detail,
    activeTab,
    setActiveTab,
    loading,
    error,
    savingNote,
    savingTag,
    open,
    close,
    toggleBookmark,
    syncBookmark,
    saveNote,
    addTag,
    removeTag,
  }
}

export default useQuestionDetail
